import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Heart, Trash2, ExternalLink, Share2 } from 'lucide-react';
import { ProductCard } from './ProductCard';
import { useWishlist } from '../contexts/WishlistContext';

export function WishlistPage() {
  const { wishlistItems, removeFromWishlist, clearWishlist, wishlistLoaded } = useWishlist();

  const shareWishlist = async () => {
    const currentUrl = window.location.href;

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'My TechieDay Wishlist',
          text: 'Check out my tech wishlist on TechieDay!',
          url: currentUrl,
        });
      } catch (err) {
        console.error('Error sharing:', err);
      }
    } else {
      try {
        await navigator.clipboard.writeText(currentUrl);
        alert('Wishlist link copied to clipboard!');
      } catch (err) {
        console.error('Failed to copy:', err);
        alert('Unable to copy the link. Please copy it manually.');
      }
    }
  };

  const title = 'My Wishlist - TechieDay';
  const description = 'Your saved tech products and gadgets. Track prices and get notified when they drop.';

  if (!wishlistLoaded) {
    return (
      <>
        <Helmet>
          <title>{title}</title>
          <meta name="description" content={description} />
          <meta property="og:title" content={title} />
          <meta property="og:description" content={description} />
          <meta property="og:type" content="website" />
          <meta property="og:url" content="/wishlist" />
        </Helmet>

        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-pink-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading your wishlist...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="/wishlist" />
      </Helmet>

      <div className="min-h-screen">
        <header className="bg-gradient-to-r from-pink-500 to-red-500 text-white">
          <div className="max-w-7xl mx-auto px-4 py-8">
            <div className="text-center">
              <div className="flex items-center justify-center mb-4">
                <Heart className="w-8 h-8 mr-3 fill-current" />
                <h1 className="text-xl sm:text-3xl font-bold text-white dark:text-gray-900">My Wishlist</h1>
              </div>
              <p className="text-xl opacity-90 max-w-3xl mx-auto">
                Your saved tech products and price tracking alerts
              </p>
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-4 py-8">
          {wishlistItems.length === 0 ? (
            <div className="text-center py-16">
              <Heart className="w-20 h-20 text-gray-300 mx-auto mb-6" />
              <h2 className="text-2xl font-semibold text-gray-700 mb-4">Your wishlist is empty</h2>
              <p className="text-gray-500 mb-8 max-w-md mx-auto">
                Start adding products to your wishlist to track prices and save your favorite items.
              </p>
              <a
                href="/"
                className="inline-flex items-center bg-pink-500 hover:bg-pink-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
              >
                Start Shopping
                <ExternalLink className="w-4 h-4 ml-2" />
              </a>
            </div>
          ) : (
            <>
              {/* Wishlist Actions */}
              <div className="mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <h2 className="text-2xl font-semibold text-gray-900">
                    {wishlistItems.length} {wishlistItems.length === 1 ? 'Item' : 'Items'} in Wishlist
                  </h2>
                  <p className="text-gray-600 mt-1">Track prices and get alerts when they drop</p>
                </div>
                
                <div className="flex gap-3">
                  <button
                    onClick={shareWishlist}
                    className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                  >
                    <Share2 className="w-4 h-4" />
                    Share
                  </button>
                  {wishlistItems.length > 0 && (
                    <button
                      onClick={clearWishlist}
                      className="flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                      Clear All
                    </button>
                  )}
                </div>
              </div>

              {/* Wishlist Items */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {wishlistItems.map((product) => (
                  <div key={product.asin} className="relative">
                    
                    {/* Create a properly formatted product object for ProductCard */}
                    <ProductCard product={product} />
                    
                    <div className="absolute top-12 right-4 z-10 flex flex-col items-end gap-2">
                      <button
                        onClick={() => removeFromWishlist(product.asin)}
                        className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-full shadow-lg transition-colors"
                        title="Remove from wishlist"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      
                      <div className="bg-white/90 backdrop-blur-sm rounded px-2 py-1 text-xs text-gray-600">
                        Added {new Date(product.addedDate).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Price Alert Info */}
              <div className="mt-12 bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-blue-900 mb-2">Price Alerts</h3>
                <p className="text-blue-800 mb-4">
                  We'll automatically notify you when prices drop on your wishlist items.
                </p>
                <div className="flex items-center gap-4 text-sm text-blue-700">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span>Email notifications enabled</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <span>Daily price monitoring</span>
                  </div>
                </div>
              </div>
            </>
          )}
        </main>
      </div>
    </>
  );
}
